import React from 'react';

export interface HighlightBarChartItem {
  label: string;
  value: number;
  iconUrl?: string;
  dotColor?: string;
  valueLabel?: string;
}

interface HighlightBarChartProps {
  items: HighlightBarChartItem[];
  className?: string;
  valueSuffix?: string;
  valueDecimals?: number;
  barColor?: string;
  adaptiveRowSpacing?: boolean;
}

const formatValue = (value: number, decimals: number, suffix: string) => {
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${formatted}${suffix}`;
};

export const HighlightBarChart: React.FC<HighlightBarChartProps> = ({
  items,
  className = '',
  valueSuffix = '',
  valueDecimals = 0,
  barColor = 'bg-zinc-400/50',
  adaptiveRowSpacing = false,
}) => {
  const maxValue = Math.max(...items.map((item) => item.value), 1);
  const containerClassName = adaptiveRowSpacing
    ? 'grid h-full auto-rows-fr content-stretch gap-1.5 sm:gap-2'
    : 'grid content-start gap-2 sm:gap-2.5';
  const rowClassName = adaptiveRowSpacing
    ? 'flex h-full items-center gap-2 sm:gap-3'
    : 'flex items-center gap-2 sm:gap-3';
  const labelClassName = adaptiveRowSpacing
    ? 'truncate text-xs font-medium leading-5 text-zinc-900 sm:text-sm sm:leading-6'
    : 'truncate text-xs font-medium text-zinc-900 sm:text-sm';

  return (
    <div className={`${containerClassName} ${className}`}>
      {items.map((item) => {
        const ratio = item.value > 0 ? Math.max(0.02, item.value / maxValue) : 0;

        return (
          <div key={item.label} className={rowClassName}>
            {/* Left: icon/dot + label — sits directly on page background, no pill */}
            <div className="flex w-24 shrink-0 items-center gap-1.5 sm:w-32 sm:gap-2 md:w-40">
              {item.iconUrl && (
                <img
                  className="h-4 w-4 shrink-0 rounded-full bg-white sm:h-[18px] sm:w-[18px]"
                  src={item.iconUrl}
                  alt=""
                  width={18}
                  height={18}
                  loading="lazy"
                />
              )}
              {item.dotColor && (
                <span
                  className="h-2 w-2 shrink-0 rounded-full sm:h-2.5 sm:w-2.5"
                  style={{ backgroundColor: item.dotColor }}
                  aria-hidden="true"
                />
              )}
              <span className={labelClassName}>{item.label}</span>
            </div>

            {/* Right: bar track + fill */}
            <div className="relative h-2.5 min-w-[2rem] flex-1 overflow-hidden rounded-full bg-zinc-200/60 sm:h-3">
              <div
                className={`absolute inset-y-0 left-0 ${barColor}`}
                style={{ width: `${ratio * 100}%` }}
              />
            </div>

            {/* Value */}
            <div className="w-8 shrink-0 text-right text-xs font-medium tabular-nums text-zinc-500 sm:w-12 sm:text-sm">
              {item.valueLabel || formatValue(item.value, valueDecimals, valueSuffix)}
            </div>
          </div>
        );
      })}
    </div>
  );
};
